import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Calendar,
  Edit2,
  LogOut,
  MessageCircle,
  X,
  Gift,
  Share2,
  Copy,
  Check,
  Trophy,
  Star,
  Clock,
  AlertTriangle,
} from "lucide-react";
import useUserData from "../../Hooks/useUserData";
import FootballLoading from "../../Utils/FootballLoading";
import { useNavigate, useParams } from "react-router-dom";
import { auth, database } from "../../Config/firebase";
import { signOut } from "../../Config/matuAuth";
import { ref, get } from "../../Config/matuRtdb";
import ReferralCodeRedemption from "./ReferralCodeRedemption";

const ProfileSection = ({
  icon: Icon,
  title,
  value,
  editable,
  onEdit,
  highlight,
}) => {
  return (
    <div
      className={`flex items-center space-x-3 p-3 ${
        highlight
          ? "bg-gradient-to-r from-gray-800 to-gray-900"
          : "bg-gray-800/80"
      } rounded-lg transition-all duration-300 hover:shadow-lg border-l-2 ${
        highlight ? "border-yellow-500" : "border-red-500"
      }`}
    >
      <div
        className={`p-2 rounded-full ${
          highlight
            ? "bg-yellow-500/20 text-yellow-400"
            : "bg-red-500/20 text-red-400"
        }`}
      >
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-grow">
        <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          {title}
        </h3>
        <p className="text-base text-white font-medium">{value}</p>
      </div>
      {editable && (
        <button
          onClick={onEdit}
          className={`p-1.5 ${
            highlight ? "bg-yellow-500" : "bg-red-500"
          } rounded-full hover:opacity-90 transition-colors duration-300`}
        >
          <Edit2 className="w-3.5 h-3.5 text-white" />
        </button>
      )}
    </div>
  );
};

const SupportBanner = ({ onClose }) => {
  const message = encodeURIComponent("Hola, necesito ayuda con mi cuenta.");
  const whatsappLink = `https://wa.me/573023580862?text=${message}`;

  return (
    <div className="relative mb-4 bg-gradient-to-r from-red-600 to-orange-500 rounded-lg shadow-lg animate-fadeIn overflow-hidden">
      <div className="absolute -bottom-8 -right-8 w-32 h-32 bg-white opacity-10 rounded-full"></div>
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-white hover:text-gray-200"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center space-x-3 p-3">
        <div className="relative">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg">
            <img
              src="https://firebasestorage.googleapis.com/v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Fnuevo%20logo.png?alt=media&token=5bcd17e0-99cd-4e7c-a45b-d2928a449a40v0/b/towin-fe4cc.appspot.com/o/logoBlgo%2Ficon512x512preview.png?alt=media&token=00445cc5-773d-43aa-862e-6c437b48bce2"
              alt="Support"
              className="w-9 h-9 rounded-full"
            />
          </div>
          <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border border-white"></div>
        </div>

        <div className="flex-1">
          <h3 className="text-sm font-bold text-white">¿Necesitas ayuda?</h3>
          <p className="text-xs text-white text-opacity-90 mb-1">
            Tu agente: <span className="font-semibold">Juan</span>
          </p>
          <a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-1 bg-white text-red-600 font-medium text-xs py-1 px-3 rounded-full hover:bg-gray-100 transition-colors duration-300"
          >
            <MessageCircle className="w-3 h-3" />
            <span>Contactar vía WhatsApp</span>
          </a>
        </div>
      </div>
    </div>
  );
};

// Error banner component
const ErrorBanner = ({ message, onRetry }) => {
  return (
    <div className="mb-4 bg-red-900/30 p-4 rounded-lg border border-red-800 animate-fadeIn">
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-red-500/20 rounded-full">
          <AlertTriangle className="w-5 h-5 text-red-400" />
        </div>
        <div className="flex-grow">
          <h3 className="text-sm font-bold text-white">Error de acceso</h3>
          <p className="text-xs text-red-400 mt-1">{message}</p>
        </div>
      </div>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 w-full bg-red-600 text-white py-1.5 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center justify-center space-x-1"
        >
          <span>Intentar nuevamente</span>
        </button>
      )}
    </div>
  );
};

const UserProfile = () => {
  const { userData, loading, error, refreshUserData } = useUserData();
  const [showSupportBanner, setShowSupportBanner] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [sharedProfile, setSharedProfile] = useState(null);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [viewingShared, setViewingShared] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const navigate = useNavigate();
  const { id: sharedProfileId } = useParams();

  // Determine if we're viewing a shared profile or our own
  useEffect(() => {
    const fetchSharedProfile = async () => {
      if (sharedProfileId) {
        setIsLoadingShared(true);
        setViewingShared(true);

        try {
          // Get user data from public profiles collection instead
          const profileRef = ref(database, `publicProfiles/${sharedProfileId}`);
          const snapshot = await get(profileRef);

          if (snapshot.exists()) {
            setSharedProfile(snapshot.val());
            setPermissionError(null);
          } else {
            setPermissionError(
              "El perfil solicitado no existe o no es público"
            );
          }
        } catch (err) {
          console.error("Error al obtener perfil compartido:", err);
          setPermissionError(
            "No tienes permisos para ver este perfil. El usuario debe configurar su perfil como público."
          );
        } finally {
          setIsLoadingShared(false);
        }
      }
    };

    if (sharedProfileId) {
      fetchSharedProfile();
    }
  }, [sharedProfileId]);

  // Generate share URL when userData is available
  useEffect(() => {
    if (userData && userData.id) {
      const baseUrl = window.location.origin;
      setShareUrl(`${baseUrl}/profile/${userData.id}`);
    } else {
      // Fallback: try to get userId from localStorage
      const userUUID = localStorage.getItem("userUUID");
      if (userUUID) {
        const baseUrl = window.location.origin;
        setShareUrl(`${baseUrl}/profile/${userUUID}`);
      }
    }
  }, [userData]);

  // Handle code redemption
  const handleCodeRedeemed = () => {
    console.log("Código redimido, actualizando datos...");
    refreshUserData();
  };

  // Handle profile sharing
  const handleShareProfile = () => {
    if (navigator.share) {
      navigator
        .share({
          title: `Perfil de ${userData?.name || "Usuario"}`,
          text: "¡Mira mi perfil en ToWin!",
          url: shareUrl,
        })
        .catch((err) => console.log("Error al compartir:", err));
    } else {
      copyToClipboard();
    }
  };

  // Copy to clipboard function
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("userUUID");
      navigate("/prediccion-zone/start");
    } catch (error) {
      console.error("Error al cerrar sesión:", error);
    }
  };

  // Return to your profile function
  const handleViewOwnProfile = () => {
    navigate("/profile");
  };

  if (isLoadingShared || (loading && !viewingShared)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900">
        <FootballLoading />
        <p className="mt-4 text-white text-opacity-80 animate-pulse">
          Cargando perfil...
        </p>
      </div>
    );
  }

  // Display error for own profile view
  if (error && !viewingShared) {
    return (
      <div className="min-h-scree flex flex-col items-center justify-center bg-gray-900 p-3">
        <div className="text-center bg-red-900/30 p-4 rounded-lg border border-red-800 max-w-md">
          <div className="text-red-500 text-5xl mb-3">!</div>
          <h2 className="text-lg font-bold text-white mb-2">
            Error al cargar perfil
          </h2>
          <p className="text-red-400 mb-3 text-sm">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-red-600 text-white py-1.5 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Intentar nuevamente
          </button>
        </div>
      </div>
    );
  }

  // Choose the correct data source - shared profile or own profile
  const profileData = viewingShared ? sharedProfile : userData;
  const isVip = profileData?.typeU === "Cuenta Vip";

  return (
    <div className="min-h-screen pb-14 bg-gradient-to-b from-gray-900 to-black py-4">
      <div className="max-w-lg mx-auto px-3">
        {/* Support Banner - only show on own profile */}
        {!viewingShared && showSupportBanner && (
          <SupportBanner onClose={() => setShowSupportBanner(false)} />
        )}

        {/* Error banner for shared profile view */}
        {viewingShared && permissionError && (
          <ErrorBanner
            message={permissionError}
            onRetry={() => window.location.reload()}
          />
        )}

        {/* Shared Profile Notice */}
        {viewingShared && !permissionError && (
          <div className="mb-4 bg-blue-900/30 p-3 rounded-lg border border-blue-800">
            <div className="flex items-center space-x-2">
              <div className="p-1.5 bg-blue-500/20 rounded-full">
                <User className="w-4 h-4 text-blue-400" />
              </div>
              <div className="flex-grow">
                <p className="text-sm text-blue-300">
                  Estás viendo el perfil público de{" "}
                  <span className="font-semibold">
                    {sharedProfile?.name || "Usuario"}
                  </span>
                </p>
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                onClick={handleViewOwnProfile}
                className="text-xs bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded transition-colors duration-300"
              >
                Volver a mi perfil
              </button>
            </div>
          </div>
        )}

        {/* Profile Card */}
        {(!viewingShared || (viewingShared && !permissionError)) && (
          <div className="bg-gray-900 rounded-xl shadow-2xl border border-gray-800 overflow-hidden">
            {/* Header */}
            <div className="relative">
              {/* Banner with animated pattern */}
              <div className="h-24 bg-gradient-to-r from-red-600 to-orange-500 relative overflow-hidden">
                <div className="absolute inset-0 opacity-30">
                  <div className="absolute top-0 left-0 right-0 h-full w-full bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.15),transparent_70%)]"></div>
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute rounded-full bg-white/10"
                      style={{
                        width: `${Math.random() * 30 + 10}px`,
                        height: `${Math.random() * 30 + 10}px`,
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `float ${
                          Math.random() * 10 + 5
                        }s ease-in-out infinite`,
                      }}
                    ></div>
                  ))}
                </div>

                {/* Share Profile Button - only on own profile */}
                {!viewingShared && (
                  <button
                    onClick={handleShareProfile}
                    className="absolute top-3 right-3 bg-white/20 hover:bg-white/30 text-white p-1.5 rounded-full transition-all duration-300 backdrop-blur-sm"
                    title="Compartir perfil"
                  >
                    {copied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>

              {/* Avatar and name */}
              <div className="flex flex-col items-center -mt-12 pb-3">
                <div
                  className={`relative border-4 ${
                    isVip ? "border-yellow-500" : "border-gray-900"
                  } rounded-full shadow-xl`}
                >
                  <img
                    src="https://avatar.iran.liara.run/public/boy"
                    alt="Profile"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                  {isVip && (
                    <div className="absolute -top-1 -right-1 bg-yellow-500 text-yellow-900 p-1 rounded-full">
                      <Star className="w-3 h-3 fill-yellow-900" />
                    </div>
                  )}
                  <div className="absolute bottom-0 right-0 bg-green-500 w-3 h-3 rounded-full border-2 border-gray-900"></div>
                </div>

                <div className="mt-2 text-center">
                  <h1 className="text-xl font-bold text-white">
                    {profileData?.name || "Sin identificar"}
                  </h1>

                  {/* Account Type Badge */}
                  <div
                    className={`mt-1 px-3 py-0.5 rounded-full text-xs font-medium inline-flex items-center ${
                      isVip
                        ? "bg-gradient-to-r from-yellow-600 to-yellow-400 text-yellow-900"
                        : "bg-red-500/20 text-red-400"
                    }`}
                  >
                    {isVip ? (
                      <Trophy className="w-3 h-3 mr-1" />
                    ) : (
                      <User className="w-3 h-3 mr-1" />
                    )}
                    {profileData?.typeU || "Usuario"}
                    {isVip && <span className="ml-1">✨</span>}
                  </div>
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="px-3 pb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-white flex items-center">
                  <User className="w-4 h-4 mr-1 text-red-400" />
                  Información de cuenta
                </h2>
                <div className="text-xs text-gray-400 flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {profileData?.createdAt
                    ? new Date(profileData.createdAt).toLocaleDateString()
                    : "No disponible"}
                </div>
              </div>

              {/* Referral Code Redemption - only on own profile */}
              {!viewingShared && (
                <div className="mb-3">
                  <ReferralCodeRedemption
                    userData={userData}
                    onCodeRedeemed={handleCodeRedeemed}
                  />
                </div>
              )}

              {/* Copy Profile URL Section - only on own profile */}
              {!viewingShared && (
                <div className="flex items-center space-x-2 bg-gray-800/50 p-2 rounded-lg mb-3 text-sm">
                  <div className="flex-grow overflow-hidden">
                    <div className="truncate text-gray-300 text-xs">
                      {shareUrl}
                    </div>
                  </div>
                  <button
                    onClick={copyToClipboard}
                    className={`p-2 ${
                      copied ? "bg-green-500" : "bg-red-500"
                    } text-white rounded-lg transition-colors duration-300`}
                  >
                    {copied ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
              )}

              {/* Profile Info Sections */}
              <div className="grid grid-cols-1 gap-2 mb-3">
                {!viewingShared && (
                  <ProfileSection
                    icon={Mail}
                    title="Correo electrónico"
                    value={profileData?.email || "Sin identificar"}
                  />
                )}
                <ProfileSection
                  icon={Phone}
                  title="Código de referido"
                  value={profileData?.referralCode || "Sin identificar"}
                  highlight={true}
                />
                <ProfileSection
                  icon={Briefcase}
                  title="Tipo de cuenta"
                  value={profileData?.typeU || "Estándar"}
                  highlight={isVip}
                />
              </div>

              {/* Stats Section */}
              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-gray-800/50 p-2 rounded-lg text-center">
                  <div className="text-xs text-gray-400">Referidos</div>
                  <div className="text-lg font-bold text-white">
                    {profileData?.referrals || 0}
                  </div>
                </div>
                <div className="bg-gray-800/50 p-2 rounded-lg text-center">
                  <div className="text-xs text-gray-400">Pronósticos</div>
                  <div className="text-lg font-bold text-white">
                    {profileData?.predictions || 0}
                  </div>
                </div>
                <div className="bg-gray-800/50 p-2 rounded-lg text-center">
                  <div className="text-xs text-gray-400">Aciertos</div>
                  <div className="text-lg font-bold text-white">
                    {profileData?.correctPredictions || 0}
                  </div>
                </div>
              </div>

              {/* Action Buttons - only on own profile */}
              {!viewingShared && (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={handleShareProfile}
                    className="flex items-center justify-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-lg transition-all duration-300 text-sm"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Compartir perfil</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg transition-all duration-300 text-sm"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Cerrar sesión</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserProfile;
